// Environment configuration
const USE_PROD_BACKEND = process.env.REACT_APP_PROD_BACKEND; // Toggle this to switch between local and production

const BASE_URL =
  USE_PROD_BACKEND === "true"
    ? "https://api.campusquest.io"
    : "http://localhost:8000";

export const API_ENDPOINTS = {
  // Base URL for custom endpoints
  base: BASE_URL,

  // Essay analysis endpoints
  analyze: `${BASE_URL}/api/analyze`,
  analyzeDoc: `${BASE_URL}/api/analyze-doc`,
  ideate: `${BASE_URL}/api/ideate`,
  chat: `${BASE_URL}/api/chat`,
  health: `${BASE_URL}/api/health`,

  // Contact/support endpoint
  contact: `${BASE_URL}/api/contact`,

  // Stripe payment endpoints
  createCheckoutSession: `${BASE_URL}/api/create-checkout-session`,
  stripeWebhook: `${BASE_URL}/api/stripe-webhook`,
  createPortalSession: `${BASE_URL}/api/create-portal-session`,

  // Stripe Connect endpoints
  createStripeAccount: `${BASE_URL}/api/stripe/create-connect-account`,
  getStripeAccountStatus: (userId: string) =>
    `${BASE_URL}/api/stripe/account-status/${userId}`,
  stripeConnectWebhook: `${BASE_URL}/api/stripe/connect-webhook`,

  // Payment processing endpoints
  createPaymentIntent: `${BASE_URL}/api/payments/create-intent`,
  confirmPayment: `${BASE_URL}/api/payments/confirm`,
  // User management endpoints
  getUser: (userId: string) => `${BASE_URL}/api/users/${userId}`,
  getAllUsers: `${BASE_URL}/api/users`,
  createUser: `${BASE_URL}/api/users`,
  updateUser: (userId: string) => `${BASE_URL}/api/users/${userId}`,
  // Profile image endpoints
  uploadProfileImage: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/profile-image`,
  deleteProfileImage: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/profile-image`,
  getUserSubscription: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/subscription`,
  getUserColleges: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/colleges`,
  getUserEssays: (userId: string) => `${BASE_URL}/api/users/${userId}/essays`,
  getUserDashboard: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/dashboard`,
  getReviewerDashboard: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/reviewer-dashboard`,
  getBrainstormSessions: (userId: string, limit = 20) =>
    `${BASE_URL}/api/users/${userId}/brainstorm-sessions?limit=${limit}`,

  // Reviewer profile endpoints
  createReviewerProfile: `${BASE_URL}/api/reviewer-profiles`,
  getReviewerProfile: (userId: string) =>
    `${BASE_URL}/api/reviewer-profiles/${userId}`,
  updateReviewerProfile: (userId: string) =>
    `${BASE_URL}/api/reviewer-profiles/${userId}`,
  getActiveReviewers: `${BASE_URL}/api/reviewers/active`,

  // Review request endpoints
  createReviewRequest: `${BASE_URL}/api/review-requests`,
  getPendingRequests: (reviewerId: string) =>
    `${BASE_URL}/api/review-requests/pending/${reviewerId}`,
  getActiveRequests: (reviewerId: string) =>
    `${BASE_URL}/api/review-requests/active/${reviewerId}`,
  getCompletedRequests: (reviewerId: string) =>
    `${BASE_URL}/api/review-requests/completed/${reviewerId}`,
  updateReviewRequest: (requestId: string) =>
    `${BASE_URL}/api/review-requests/${requestId}`,
  deleteReviewRequest: (requestId: string) =>
    `${BASE_URL}/api/review-requests/${requestId}`,
  getReviewRequestDetails: (requestId: string) =>
    `${BASE_URL}/api/review-requests/${requestId}/details`,
  getStudentRequests: (studentId: string) =>
    `${BASE_URL}/api/review-requests/student/${studentId}`,
  getUserEarnings: (userId: string) => `${BASE_URL}/api/earnings/${userId}`,

  getUserInsights: (userId: string) => `${BASE_URL}/api/insights/${userId}`,

  // Referral endpoints
  createReferralCode: (userId: string) =>
    `${BASE_URL}/api/referrals/create-code?referrer_id=${userId}`,
  getReferralStats: (userId: string) =>
    `${BASE_URL}/api/referrals/stats/${userId}`,
  processReferralSignup: `${BASE_URL}/api/referrals/process-signup`,
  processProConversion: `${BASE_URL}/api/referrals/process-pro-conversion`,

  // College endpoints
  createCollege: `${BASE_URL}/api/colleges`,
  updateCollege: (collegeId: string) => `${BASE_URL}/api/colleges/${collegeId}`,
  deleteCollege: (collegeId: string) => `${BASE_URL}/api/colleges/${collegeId}`,

  // Essay endpoints
  createEssay: `${BASE_URL}/api/essays`,
  getEssay: (essayId: string) => `${BASE_URL}/api/essays/${essayId}`,
  updateEssay: (essayId: string) => `${BASE_URL}/api/essays/${essayId}`,
  deleteEssay: (essayId: string) => `${BASE_URL}/api/essays/${essayId}`,

  // Essay sharing endpoints
  shareEssay: `${BASE_URL}/api/essay-shares`,
  getEssayShares: (essayId: string) =>
    `${BASE_URL}/api/essays/${essayId}/shares`,
  getUserSharedEssays: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/shared-essays`,
  updateEssayShare: (shareId: string) =>
    `${BASE_URL}/api/essay-shares/${shareId}`,
  deleteEssayShare: (shareId: string) =>
    `${BASE_URL}/api/essay-shares/${shareId}`,
  getUserEssayPermission: (essayId: string, userId: string) =>
    `${BASE_URL}/api/essays/${essayId}/permissions/${userId}`,

  // Public sharing endpoints
  getEssayPublicShareLink: (essayId: string, userId?: string) =>
    `${BASE_URL}/api/essays/${essayId}/public-share${
      userId ? `?user_id=${userId}` : ""
    }`,
  getPublicShare: (shareToken: string) =>
    `${BASE_URL}/api/public-shares/${shareToken}`,
  toggleEssayPublicSharing: (essayId: string, userId: string) =>
    `${BASE_URL}/api/essays/${essayId}/public-share/toggle?user_id=${userId}`,
  incrementPublicShareView: (shareToken: string) =>
    `${BASE_URL}/api/public-shares/${shareToken}/view`,

  // College Planning endpoints
  getPlanningColleges: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/planning/colleges`,
  createPlanningCollege: `${BASE_URL}/api/planning/colleges`,
  updatePlanningCollege: (collegeId: string) =>
    `${BASE_URL}/api/planning/colleges/${collegeId}`,
  deletePlanningCollege: (collegeId: string) =>
    `${BASE_URL}/api/planning/colleges/${collegeId}`,

  // Planning Milestone endpoints
  getPlanningMilestones: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/planning/milestones`,
  createPlanningMilestone: `${BASE_URL}/api/planning/milestones`,
  updatePlanningMilestone: (milestoneId: string) =>
    `${BASE_URL}/api/planning/milestones/${milestoneId}`,
  deletePlanningMilestone: (milestoneId: string) =>
    `${BASE_URL}/api/planning/milestones/${milestoneId}`,

  // Planning Event endpoints
  getPlanningEvents: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/planning/events`,
  createPlanningEvent: `${BASE_URL}/api/planning/events`,
  updatePlanningEvent: (eventId: string) =>
    `${BASE_URL}/api/planning/events/${eventId}`,
  deletePlanningEvent: (eventId: string) =>
    `${BASE_URL}/api/planning/events/${eventId}`,

  // Scholarship endpoints
  SCHOLARSHIPS: `${BASE_URL}/api/scholarships`,
  searchScholarships: `${BASE_URL}/api/scholarships/search`,
  getScholarship: (scholarshipId: string) =>
    `${BASE_URL}/api/scholarships/${scholarshipId}`,
  saveScholarship: (scholarshipId: string) =>
    `${BASE_URL}/api/scholarships/${scholarshipId}/save`,
  getUserScholarships: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/scholarships`,
  matchScholarships: (userId: string) =>
    `${BASE_URL}/api/scholarships/match?user_id=${userId}`,

  // University/College endpoints
  getAllUniversityNames: `${BASE_URL}/api/universities/names`,
  searchUniversities: `${BASE_URL}/api/universities/search`,
  getUniversity: (collegeName: string) =>
    `${BASE_URL}/api/universities/${encodeURIComponent(collegeName)}`,
  saveCollege: (collegeName: string) =>
    `${BASE_URL}/api/colleges/${encodeURIComponent(collegeName)}/save`,
  getSavedColleges: (userId: string) =>
    `${BASE_URL}/api/users/${userId}/saved-colleges`,
};

export default API_ENDPOINTS;
